<template>
  <div v-if="!hide">
    <can I="read" a="OpportunityApplication">
      <v-card tile flat class="application-card" style="cursor: pointer;">
        <v-layout align-start justify-start row fill-height class="py-3">
          <v-flex xs2 class="text-xs-center">
            <v-layout align-center justify-start row fill-height>
              <v-flex xs3>
                <v-checkbox
                  v-if="!isWithdrawn"
                  :input-value="isSelected"
                  @click.stop="toggle(application)"
                  class="pl-2"
                  color="primary"
                >
                </v-checkbox>
              </v-flex>
              <v-flex>
                <v-progress-circular
                  :value="Number(application.completion)"
                  :size="90"
                  :width="5"
                  :rotate="-90"
                  :color="application.complete ? 'success' : 'warning'"
                >
                  <v-avatar size="80">
                    <img :src="application.profile.avatar" />
                  </v-avatar>
                  <v-avatar
                    v-if="featuredQualification"
                    size="40"
                    style="position: absolute; top: 45px; right: -15px;"
                  >
                    <img
                      v-if="
                        !featuredQualification.institution ||
                          !featuredQualification.institution.logo
                      "
                      src="/img/missing/logos/institutions/original.png"
                    />
                    <img v-else :src="featuredQualification.institution.logo" />
                  </v-avatar>
                </v-progress-circular>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs5>
            <v-card-title primary-title class="py-0" @click="$emit('click', application.id)">
              <div>
                <div class="headline">
                  {{ application.profile.firstName }} {{ application.profile.lastName }}
                  <v-tooltip
                    bottom
                    class="px-1"
                    color="grey darken-3"
                    v-if="application.eligibility && application.eligibility.referredAt"
                  >
                    <span slot="activator"
                      ><v-icon class="text-vertical-middle">mdi-account-star</v-icon></span
                    >
                    <span>Referred Candidate</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="application.complete === false">
                    <span slot="activator">
                      <v-icon class="text-vertical-middle" color="warning">
                        mdi-account
                      </v-icon>
                    </span>
                    <span>Incomplete Profile</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="!allQuestionnairesCompleted">
                    <span slot="activator">
                      <v-icon class="text-vertical-middle" color="warning">
                        mdi-clipboard-alert
                      </v-icon>
                    </span>
                    <span>Incomplete Prescreening Questions</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="application.eligibility && application.eligibility.eligible === false"
                    bottom
                  >
                    <span slot="activator"
                      ><v-icon class="text-vertical-middle">mdi-alert-circle</v-icon></span
                    >
                    Ineligible
                  </v-tooltip>
                  <span v-if="application.psychometrics">
                    <span v-for="psych in application.psychometrics" :key="`psych-${psych.id}`">
                      <v-tooltip
                        right
                        v-if="psych.show || psych.provider != 'Odyssey' || psych.aggregate >= 2"
                      >
                        <v-chip
                          small
                          slot="activator"
                          :color="psych.provider == 'Odyssey' ? recommendationColor(psych) : ''"
                          class="font-weight-bold white--text"
                        >
                          <span
                            v-if="psych.provider == 'Odyssey'"
                            v-text="recommendationText(psych)"
                          ></span>
                          <span v-else>{{ psych.aggregate }}</span>
                        </v-chip>
                        <span v-if="psych.provider == 'Odyssey'">
                          Recommendation for {{ psych.title }} positions:
                          <strong>{{ recommendationText(psych) }}</strong
                          ><br />
                          <span v-for="desc in recommendationDescription(psych)" :key="desc">
                            <br />
                            {{ desc }}
                          </span>
                        </span>
                        <span v-else>{{ psych.title || 'Aggregate' }}</span>
                      </v-tooltip>
                    </span>
                  </span>
                  <v-tooltip
                    v-if="application.profile.financial && application.profile.financial.complete"
                    bottom
                  >
                    <v-chip small slot="activator">
                      {{ application.profile.financial.currency }}
                      {{
                        application.profile.financial.incomes
                          .map(i => i.grossIncome)
                          .reduce((acc, inc) => acc + inc, 0)
                      }}
                    </v-chip>
                    <span
                      >Gross monthly income for
                      {{ application.profile.financial.dependents }} dependents</span
                    >
                  </v-tooltip>
                  <Flag
                    icon
                    v-for="(flag, i) in application.profile.flags"
                    :key="`flag-${i}`"
                    :value="flag"
                  />
                  <v-tooltip
                    v-if="application.profile && application.profile.verifiedAt"
                    color="grey darken-3"
                    class="px-0"
                    bottom
                  >
                    <span slot="activator">
                      <v-icon class="text-vertical-middle">mdi-check-decagram</v-icon>
                    </span>
                    <span>Verified by Leaply</span>
                  </v-tooltip>
                </div>
                <div v-if="featuredQualification" class="caption">
                  {{ featuredQualification.qualificationTypeName }}
                  <span v-if="featuredQualification.specialization">
                    in {{ featuredQualification.specialization }}
                  </span>
                </div>
                <div
                  v-if="featuredQualification && featuredQualification.institutionName"
                  class="caption"
                >
                  from {{ featuredQualification.institutionName }}
                </div>
                <div class="rating-wrapper mt-2">
                  <ApplicationRating :application="application" :readonly="isWithdrawn" />
                </div>
              </div>
            </v-card-title>
          </v-flex>
          <v-flex xs5 class="text-xs-right caption mr-3">
            <span
              >Applied {{ new Date(application.createdAt) | inWords({ addSuffix: true }) }}</span
            >
            <v-layout align-center justify-end class="py-2">
              <ApplicationActivityOverview :application="application" />
              <v-divider
                v-if="feedbackRequests.length && application.activitiesOverview.length"
                class="pa-2 my-1 mr-2"
                vertical
              />
              <FeedbackRequestsOverview :application-id="application.id" />
            </v-layout>
          </v-flex>
        </v-layout>
        <can I="edit" a="OpportunityApplication">
          <CandidateQuickActionToolbar
            on-hover
            v-if="!isWithdrawn"
            :candidate="application"
            color="transparent"
          />
        </can>
      </v-card>
      <v-divider />
    </can>
  </div>
</template>

<script>
// Components
import ApplicationActivityOverview from '@/components/applications_tracker/applications/ApplicationActivityOverview'
import ApplicationRating from '@/components/applications_tracker/applications/ApplicationRating'
import CandidateQuickActionToolbar from '@/components/applications_tracker/CandidateQuickActionToolbar'
import FeedbackRequestsOverview from '@/components/feedback_requests/FeedbackRequestsOverview'
import Flag from '@/components/flags/Flag'

// Helpers
import { mapGetters } from 'vuex'
import { byEndsAt, byLevels } from '@/sorting/ProfileQualification'
import _ from 'lodash'

export default {
  props: { application: Object, phase: Object, canEdit: Boolean },
  components: {
    ApplicationActivityOverview,
    ApplicationRating,
    CandidateQuickActionToolbar,
    FeedbackRequestsOverview,
    Flag,
  },
  data() {
    return {
      hide: false,
    }
  },
  computed: {
    feedbackRequests() {
      if (!_.get(this, 'application.id')) return []
      return this.$store.getters['admin/feedbackRequests/forApplication'](this.application.id)
    },

    phaseQuestionnaireIds() {
      if (this.phase && this.phase.behaviors) {
        return this.phase.behaviors.map(b => (b.questionnaireId ? b.questionnaireId : null))
      }

      return null
    },
    allQuestionnairesCompleted() {
      if (!this.phaseQuestionnaireIds || !this.phaseQuestionnaireIds[0]) return true
      if (_.get(this.application, 'questionnaires.completed')) {
        return (
          this.phaseQuestionnaireIds.filter(
            id => this.application.questionnaires.completed.indexOf(id) === -1
          ).length === 0
        )
      }

      return false
    },
    featuredQualification() {
      if (_.get(this.application, 'profile.profileQualification')) {
        // Use featured if available
        return this.application.profile.profileQualification
      } else if (_.get(this.application, 'profile.profileQualifications[0]')) {
        // Otherwise find featured in list
        const qualifications = JSON.parse(
          JSON.stringify(this.application.profile.profileQualifications)
        )
        return qualifications.filter(pq => pq.featured)[0]
      } else {
        // Otherwise show none
        return null
      }
    },
    selected() {
      return this.$store.getters['admin/applications/selected']
    },
    isSelected() {
      return this.selected.indexOf(this.application.id) >= 0
    },
    anySelected() {
      return this.selected[0] != undefined
    },
    isWithdrawn() {
      return this.application.status == 'Withdrawn'
    },
  },
  methods: {
    toggle(application) {
      if (!this.isSelected) this.select(application.id)
      else this.deselect(application.id)
    },

    select(id) {
      if (this.isWithdrawn) {
        this.$store.dispatch('snackbarShow', 'No bulk action available')
        return
      }
      this.$store.commit('admin/applications/select', id)
    },

    deselect(id) {
      this.$store.commit('admin/applications/deselect', id)
    },
    recommendationText(psych) {
      switch (psych.aggregate) {
        case 4:
          return 'Excellent Fit'
        case 3:
          return 'Strong Fit'
        case 2:
          return 'Moderate Fit'
        case 1:
          return 'Weak Fit'
        case 0:
          return 'No Fit'
        default:
          return ''
      }
    },
    recommendationColor(psych) {
      switch (psych.aggregate) {
        case 4:
          return 'green darken-1'
        case 3:
          return 'light-green darken-1'
        case 2:
          return 'lime darken-1'
        case 1:
          return 'orange darken-2'
        case 0:
          return 'red'
        default:
          return ''
      }
    },
    recommendationDescription(psych) {
      switch (psych.aggregate) {
        case 4:
          return [
            'Demonstrates a high skill level to cope with the complexity of the work and a high level of interest and enjoyment for the work.',
            'There is almost no clear risk of employment.',
          ]
        case 3:
          return [
            'Demonstrates a moderate to high skill level to cope with the complexity of the work and a good level of interest and enjoyment for the work.',
            'There is slight or limited risk associated with employment.',
          ]
        case 2:
          return [
            'Demonstrates a moderate to high skill level to cope with the complexity of the work and a moderate level of enjoyment and interest for the work OR',
            'Demonstrates a low to moderate skill level and a good level of enjoyment and interest for the work.',
            'There are some risks associated with employment but still considered a good candidate.',
          ]
        case 1:
          return [
            'Demonstrates a low to moderate skill level to cope with the complexity of the work and moderate level of enjoyment and interest for the work.',
            'There are clear risks associated with employment.',
          ]
        case 0:
          return [
            'Demonstrates a low skill level to cope with the complexity of the work OR',
            'Demonstrates low enjoyment and interest in the work.',
            'There are several risks associated with employment.',
          ]
        default:
          return []
      }
    },
  },
}
</script>

<style>
.application-card div.rating-wrapper > span > fieldset {
  position: relative;
  z-index: 100;
}

.application-card:hover,
.application-card:hover .application-tracker.quick-action-toolbar div.show-on-hover {
  opacity: 1;
  background-color: #333;
}
</style>
